import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Search, Plus, Edit, Trash2, CheckCircle, AlertTriangle, AlertCircle, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, CheckSquare, Square, LayoutGrid, Layers, Clock, Percent, FileText, Shield, Tag } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useTranslation } from 'react-i18next';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
// Tables-mobile.css - DELETED (not needed)

// Register locales
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

/**
 * Inline edit cell — MUST stay outside ConditionsPaiementPage.
 * Defining this inside the parent makes React see a new component type every render,
 * so each keystroke remounts the input, re-runs focus+select(), and replaces text (blue selection bug).
 */
function PaymentTermEditableCell({
  recordId,
  field,
  value,
  className = 'text-white font-semibold',
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  editTitle,
  readOnly = true,
  displayValue,
}) {
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            skipBlurSaveRef.current = true;
            onSave(recordId, field, e.currentTarget.value);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }


  const paymentTermsFormTabs = useMemo(() => ([
    { id: 'identity', label: p('form_section_identity'), icon: Tag, iconColor: 'text-blue-400' },
    { id: 'notes', label: p('form_section_notes'), icon: FileText, iconColor: 'text-amber-400' },
  ]), [p]);

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(recordId, field, value);
        }
      }}
      className={`${className} cursor-pointer`}
      title={editTitle}
    >
      {value || '-'}
    </span>
  );
}

const PAYMENT_TERM_MODAL_SHELL = 'bg-white border border-gray-200 backdrop-blur-lg rounded-2xl';
const PAYMENT_TERM_MODAL_SHADOW = {
  boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)',
};
const PAYMENT_TERM_FIELD =
  'w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all';
const PAYMENT_TERM_LABEL = 'block text-gray-600 text-sm mb-2 font-medium';
const PAYMENT_TERM_SECTION = 'bg-gray-50 rounded-xl border border-gray-200 overflow-hidden';

const ConditionsPaiementPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.conditionsPaiementPage.${key}`, opts);
  const brandLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const qawaem = isSaudiBrand();
  const saudiBrand = qawaem;
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useFacturationModalBodyLock(showModal, showViewModal);
  
  // Inline editing states
  const [editingCell, setEditingCell] = useState(null); // { recordId, field }
  const [editValue, setEditValue] = useState('');
  
  const [paymentTermsFormTab, setPaymentTermsFormTab] = useState('identity');
  
  const [formData, setFormData] = useState({
    term_code: '',
    label: '',
    days: '',
    discount_percent: '',
    discount_days: '',
    description: '',
    is_default: '',
    status: '',
    notes: '',
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_terms');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'شرط الدفع' });
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadPaymentTerms(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPaymentTerms = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/conditions-paiement');
      const rows = response?.data || response || [];
      setPaymentTerms(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading paymentTerms:', error);
      setPaymentTerms([]);
    }
  };

  const stats = {
    total: paymentTerms.length,
    active: paymentTerms.filter((t) => t.status === 'active').length,
    net30: paymentTerms.filter((t) => parseInt(t.days, 10) === 30 || String(t.term_code || '').toUpperCase().includes('NET30')).length,
    net60: paymentTerms.filter((t) => parseInt(t.days, 10) === 60 || String(t.term_code || '').toUpperCase().includes('NET60')).length,
    avecEscompte: paymentTerms.filter((t) => parseFloat(t.discount_percent) > 0).length,
  };

  const mainTabs = [
    { id: 'all_terms', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_delais', name: p('tab_by_terms'), icon: Clock },
    { id: 'par_escompte', name: p('tab_by_discount'), icon: Percent },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'net30', name: p('filter_net30'), icon: Calendar },
    { id: 'net60', name: p('filter_net60'), icon: Calendar },
    { id: 'discount', name: p('filter_discount'), icon: Percent },
  ];

  const getDelaisDistribution = () => {
    const net30 = paymentTerms.filter((t) => parseInt(t.days, 10) === 30).length;
    const net60 = paymentTerms.filter((t) => parseInt(t.days, 10) === 60).length;
    const other = paymentTerms.filter((t) => parseInt(t.days, 10) !== 30 && parseInt(t.days, 10) !== 60).length;
    return [
      { id: 'net30', name: p('dist_net30'), count: net30, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { id: 'net60', name: p('dist_net60'), count: net60, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      { id: 'other', name: p('dist_other_terms'), count: other, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    ];
  };

  const getEscompteDistribution = () => {
    const avec = paymentTerms.filter((t) => parseFloat(t.discount_percent) > 0).length;
    const sans = paymentTerms.filter((t) => !parseFloat(t.discount_percent)).length;
    return [
      { id: 'with_discount', name: p('dist_with_discount'), count: avec, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      { id: 'without_discount', name: p('dist_without_discount'), count: sans, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
    ];
  };

  const paymentTermDistribution = {
    all_terms: {
      title: p('overview_title'),
      data: [
        { id: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'net30', name: p('dist_net30'), count: stats.net30, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'net60', name: p('dist_net60'), count: stats.net60, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { id: 'with_discount', name: p('dist_with_discount'), count: stats.avecEscompte, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { id: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'inactive', name: p('dist_inactive'), count: paymentTerms.filter((t) => t.status === 'inactive').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_delais: {
      title: p('dist_by_terms_title'),
      data: getDelaisDistribution(),
    },
    par_escompte: {
      title: p('dist_by_discount_title'),
      data: getEscompteDistribution(),
    },
  };

  const distributionInfo = paymentTermDistribution[mainViewMode] || paymentTermDistribution.all_terms;

  const columnFilterRules = useMemo(() => [
    textColumnRule('term_code', (row) => row.term_code),
    textColumnRule('label', (row) => row.label),
    textColumnRule('days', (row) => String(row.days ?? '')),
    textColumnRule('discount_percent', (row) => String(row.discount_percent ?? '')),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredPaymentTerms = useMemo(() => {
    const base = paymentTerms.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const createdDate = new Date(f.created_at);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (createdDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (createdDate > endOfDay) return false;
      }
    }
    
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const hay = [f.term_code, f.label, f.description, f.notes].map((v) => String(v || '').toLowerCase()).join(' ');
      if (!hay.includes(q)) return false;
    }
    if (mainViewMode === 'all_terms') {
      if (quickFilter === 'active' && f.status !== 'active') return false;
      if (quickFilter === 'net30' && parseInt(f.days, 10) !== 30) return false;
      if (quickFilter === 'net60' && parseInt(f.days, 10) !== 60) return false;
      if (quickFilter === 'discount' && !(parseFloat(f.discount_percent) > 0)) return false;
    }
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_terms':
        case 'par_statut':
          if (activeDistributionFilter === 'active' && f.status !== 'active') return false;
          if (activeDistributionFilter === 'inactive' && f.status !== 'inactive') return false;
          if (activeDistributionFilter === 'net30' && parseInt(f.days, 10) !== 30) return false;
          if (activeDistributionFilter === 'net60' && parseInt(f.days, 10) !== 60) return false;
          if (activeDistributionFilter === 'with_discount' && !(parseFloat(f.discount_percent) > 0)) return false;
          break;
        case 'par_delais':
          if (activeDistributionFilter === 'net30' && parseInt(f.days, 10) !== 30) return false;
          if (activeDistributionFilter === 'net60' && parseInt(f.days, 10) !== 60) return false;
          if (activeDistributionFilter === 'other' && (parseInt(f.days, 10) === 30 || parseInt(f.days, 10) === 60)) return false;
          break;
        case 'par_escompte':
          if (activeDistributionFilter === 'with_discount' && !(parseFloat(f.discount_percent) > 0)) return false;
          if (activeDistributionFilter === 'without_discount' && parseFloat(f.discount_percent) > 0) return false;
          break;
        default:
          break;
      }
    }
    return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [paymentTerms, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, filters, columnFilterRules]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        term_code: formData.term_code || undefined,
        label: formData.label,
        days: parseInt(formData.days, 10) || 30,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        discount_days: parseInt(formData.discount_days, 10) || 0,
        description: formData.description,
        is_default: formData.is_default === 'true' || formData.is_default === true,
        status: formData.status || 'active',
        notes: formData.notes,
      };
      if (editingRecord) {
        await localApiAdapter.put(`/facturation/conditions-paiement/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/facturation/conditions-paiement', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('error_save'));
    } finally {
      resetForm();
      loadPaymentTerms();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      term_code: record.term_code || '',
      label: record.label || '',
      days: record.days != null ? String(record.days) : '',
      discount_percent: record.discount_percent != null ? String(record.discount_percent) : '',
      discount_days: record.discount_days != null ? String(record.discount_days) : '',
      description: record.description || '',
      is_default: record.is_default ? 'true' : '',
      status: record.status || '',
      notes: record.notes || '',
    });
    setPaymentTermsFormTab('identity');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/facturation/conditions-paiement/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadPaymentTerms();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('error_save'));
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/facturation/conditions-paiement/${id}`, { status: newStatus });
      loadPaymentTerms();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Inline editing functions
  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    // Don't save if value hasn't changed or is being cleared intentionally
    const record = paymentTerms.find(f => f.id === recordId);
    if (!record) {
      cancelEditing();
      return;
    }

    const originalValue = record[field];
    
    if (value === String(originalValue ?? '') || !String(value).trim()) {
      cancelEditing();
      return;
    }

    try {
      let updateData = { [field]: value };
      if (field === 'days' || field === 'discount_days') {
        updateData = { [field]: parseInt(value, 10) || 0 };
      } else if (field === 'discount_percent') {
        updateData = { [field]: parseFloat(value) || 0 };
      }

      await localApiAdapter.put(`/facturation/conditions-paiement/${recordId}`, updateData);
      await loadPaymentTerms();
      cancelEditing();
      notifyUpdated(showNotify, entityLabel);
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
      notifyError(showNotify, p('error_save'));
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/facturation/conditions-paiement/${id}`)));
        setSelectedRecords([]);
        loadPaymentTerms();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      term_code: '',
      label: '',
      days: '',
      discount_percent: '',
      discount_days: '',
      description: '',
      is_default: '',
      status: '',
      notes: '',
    });
    setEditingRecord(null);
    setShowModal(false);
    setPaymentTermsFormTab('identity');
  };

  const exportHeaders = [
    { key: 'term_code', label: p('export_col_code') },
    { key: 'label', label: p('export_col_label') },
    { key: 'days', label: p('export_col_days') },
    { key: 'discount_percent', label: p('export_col_discount') },
    { key: 'discount_days', label: p('export_col_discount_days') },
    { key: 'description', label: p('export_col_description') },
    { key: 'is_default', label: p('export_col_default') },
    { key: 'status', label: p('export_col_status') },
    { key: 'notes', label: p('export_col_notes') },
  ];

  // Export handlers
  const handleExportCSV = () => {
    const dataToExport = selectedRecords.length > 0
      ? paymentTerms.filter((f) => selectedRecords.includes(f.id))
      : filteredPaymentTerms;

    exportToCSV(dataToExport, exportHeaders, `conditions-paiement_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const dataToExport = selectedRecords.length > 0
      ? paymentTerms.filter((f) => selectedRecords.includes(f.id))
      : filteredPaymentTerms;

    await exportToExcel(dataToExport, exportHeaders, `conditions-paiement_${new Date().toISOString().split('T')[0]}.xlsx`, p('export_sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dataToExport = selectedRecords.length > 0
        ? paymentTerms.filter((f) => selectedRecords.includes(f.id))
        : filteredPaymentTerms;

      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: p('export_col_code'), value: (f) => f.term_code },
          { label: p('export_col_label'), value: (f) => f.label },
          { label: p('export_col_days'), value: (f) => f.days },
          { label: p('export_col_discount'), value: (f) => f.discount_percent },
          { label: p('export_col_status'), value: (f) => f.status },
        ],
        listExportTitle: p('export_report_title'),
        clientTitle: p('export_client_title'),
        printDocumentTitle: `${p('export_client_title')} - ${new Date().toISOString().split('T')[0]}`,
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('error_export_pdf'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleImportCSV = () => {
    const schema = {
      requiredFields: ['label'],
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, exportHeaders, schema);

        if (result.errors.length > 0) {
          alert(p('import_with_errors', { valid: result.validCount, invalid: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(p('import_success', { count: result.validCount }));
        }

        if (result.data.length > 0) {
          for (const item of result.data) {
            await localApiAdapter.post('/facturation/conditions-paiement', item);
          }
          loadPaymentTerms();
        }

        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(p('error_import'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    const schema = {
      requiredFields: ['label'],
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, exportHeaders, schema);

        if (result.errors.length > 0) {
          alert(p('import_with_errors', { valid: result.validCount, invalid: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(p('import_success', { count: result.validCount }));
        }

        if (result.data.length > 0) {
          for (const item of result.data) {
            await localApiAdapter.post('/facturation/conditions-paiement', item);
          }
          loadPaymentTerms();
        }

        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(p('error_import'));
      }
    }, '.xlsx,.xls');
  };

  // Export JSON
  const handleExportJSON = () => {
    const dataToExport = selectedRecords.length > 0 
      ? paymentTerms.filter(f => selectedRecords.includes(f.id))
      : filteredPaymentTerms;
    
    const jsonContent = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conditions-paiement_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Export XML
  const handleExportXML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? paymentTerms.filter(f => selectedRecords.includes(f.id))
      : filteredPaymentTerms;
    
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<conditions_paiement>\n';
    dataToExport.forEach(item => {
      xmlContent += '  <condition>\n';
      Object.keys(item).forEach(key => {
        const value = item[key] || '';
        xmlContent += `    <${key}>${value}</${key}>\n`;
      });
      xmlContent += '  </condition>\n';
    });
    xmlContent += '</conditions_paiement>';
    
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conditions-paiement_${new Date().toISOString().split('T')[0]}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Export HTML
  const handleExportHTML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? paymentTerms.filter(f => selectedRecords.includes(f.id))
      : filteredPaymentTerms;
    
    let htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${p('export_report_title')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #1e40af; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #3b82f6; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
  </style>
</head>
<body>
  <h1>${p('export_report_title')}</h1>
  <p>${p('export_html_date', { date: new Date().toLocaleDateString(brandLocale) })}</p>
  <p>${p('export_html_total', { count: dataToExport.length })}</p>
  <table>
    <thead>
      <tr>
        <th>${p('export_col_code')}</th>
        <th>${p('export_col_label')}</th>
        <th>${p('export_col_days')}</th>
        <th>${p('export_col_discount')}</th>
        <th>${p('export_col_status')}</th>
      </tr>
    </thead>
    <tbody>`;
    
    dataToExport.forEach(f => {
      htmlContent += `
      <tr>
        <td>${f.term_code || ''}</td>
        <td>${f.label || ''}</td>
        <td>${f.days || ''}</td>
        <td>${f.discount_percent || ''}</td>
        <td>${f.status === 'active' ? p('status_active') : p('status_inactive')}</td>
      </tr>`;
    });
    
    htmlContent += `
    </tbody>
  </table>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conditions-paiement_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Export TXT
  const handleExportTXT = () => {
    const dataToExport = selectedRecords.length > 0 
      ? paymentTerms.filter(f => selectedRecords.includes(f.id))
      : filteredPaymentTerms;
    
    let txtContent = `${p('export_txt_title')}\nDate: ${new Date().toLocaleDateString(brandLocale)}\nTotal: ${dataToExport.length} ${p('export_txt_count_label')}\n\n`;
    txtContent += '='.repeat(100) + '\n\n';
    
    dataToExport.forEach(f => {
      txtContent += `Code: ${f.term_code || ''}\n`;
      txtContent += `Libellé: ${f.label || ''}\n`;
      txtContent += `Jours: ${f.days || ''}\n`;
      txtContent += `Escompte: ${f.discount_percent || 0}%\n`;
      txtContent += `${p('export_col_status')}: ${f.status === 'active' ? p('status_active') : p('status_inactive')}\n`;
      txtContent += '-'.repeat(100) + '\n\n';
    });
    
    const blob = new Blob([txtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conditions-paiement_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Import JSON
  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        
        if (!Array.isArray(data)) {
          alert(p('import_json_invalid'));
          return;
        }
        
        for (const item of data) {
          await localApiAdapter.post('/facturation/conditions-paiement', item);
        }
        await loadPaymentTerms();
        alert(p('import_success', { count: data.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('JSON import error:', error);
        alert(p('error_import_json'));
      }
    }, '.json');
  };

  // Import XML
  const handleImportXML = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'text/xml');
        
        const nodes = xmlDoc.getElementsByTagName('condition');
        let count = 0;
        
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          const item = {
            term_code: node.getElementsByTagName('term_code')[0]?.textContent || '',
            label: node.getElementsByTagName('label')[0]?.textContent || '',
            days: parseInt(node.getElementsByTagName('days')[0]?.textContent || '30', 10),
            discount_percent: parseFloat(node.getElementsByTagName('discount_percent')[0]?.textContent || '0'),
            discount_days: parseInt(node.getElementsByTagName('discount_days')[0]?.textContent || '0', 10),
            description: node.getElementsByTagName('description')[0]?.textContent || '',
            status: node.getElementsByTagName('status')[0]?.textContent || 'active',
            notes: node.getElementsByTagName('notes')[0]?.textContent || '',
          };
          if (item.label) {
            await localApiAdapter.post('/facturation/conditions-paiement', item);
            count += 1;
          }
        }
        
        await loadPaymentTerms();
        alert(p('import_success', { count }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('XML import error:', error);
        alert(p('error_import_xml'));
      }
    }, '.xml');
  };

  // Import TXT/TSV
  const handleImportTXT = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const lines = content.split('\n').filter(line => line.trim());
        
        // Detect delimiter (tab or comma)
        const delimiter = lines[0].includes('\t') ? '\t' : ',';
        
        // Parse header
        const headers = lines[0].split(delimiter).map(h => h.trim());
        
        // Parse data
        let count = 0;
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(delimiter);
          const item = {};
          headers.forEach((header, index) => {
            const key = header.toLowerCase().replace(/\s+/g, '_').replace('é', 'e');
            item[key] = values[index]?.trim() || '';
          });
          if (item.label) {
            await localApiAdapter.post('/facturation/conditions-paiement', item);
            count += 1;
          }
        }
        
        await loadPaymentTerms();
        alert(p('import_success', { count }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('TXT import error:', error);
        alert(p('error_import_txt'));
      }
    }, '.txt,.tsv');
  };

  const openNewPaymentTermModal = () => {
    setFormData(
      saudiBrand
        ? {
            term_code: 'NET30',
            label: p('form_label_ph'),
            days: '30',
            discount_percent: '',
            discount_days: '',
            description: p('form_description_ph'),
            is_default: '1',
            status: 'active',
            notes: '',
          }
        : {
            term_code: '',
            label: '',
            days: '',
            discount_percent: '',
            discount_days: '',
            description: '',
            is_default: '',
            status: '',
            notes: '',
          },
    );
    setEditingRecord(null);
    setPaymentTermsFormTab('identity');
    setShowModal(true);
  };

  
  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredPaymentTerms);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredPaymentTerms.map((r) => r.id), [filteredPaymentTerms]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style} = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total'), value: stats.total, icon: Layers, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'net30', title: p('stat_net30'), value: stats.net30, icon: Calendar, color: 'from-purple-500 to-purple-600' },
    { key: 'net60', title: p('stat_net60'), value: stats.net60, icon: Calendar, color: 'from-indigo-500 to-indigo-600' },
    { key: 'discount', title: p('stat_with_discount'), value: stats.avecEscompte, icon: Percent, color: 'from-orange-500 to-orange-600', className: 'max-lg:hidden' },
  ], [stats, p]);

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_active') || p('status_values.active') || p('dist_active') },
    { value: 'inactive', label: p('status_inactive') || p('status_values.inactive') || p('dist_inactive') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? (p('status_label') || p('status_label') || p('col_status'))
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status_label'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'term_code', label: p('col_code'), required: true },
    { key: 'label', label: p('col_label') },
    { key: 'days', label: p('col_days') },
    { key: 'discount_percent', label: p('col_discount') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 1 }),
    [qawaemBaseColumns, showCol],
  );
  return (
    <div className={`space-y-3 employees-container`}>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
<FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Calendar}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={openNewPaymentTermModal}
      createLabel={p('new_condition')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
<div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_terms' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.name ? null : item.name)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.name ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.name ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.name ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })()}% {t('common.of_total', 'من الإجمالي')}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={p('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLight={isDashboardLightMode}
              />
              
              <div className="relative" ref={statusFilterRef}>
                <button 
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">
                    {statusFilter === 'all' ? p('status_label') : statusFilter === 'active' ? p('status_active') : p('status_inactive')}
                  </span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', 
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <Filter size={16} className="text-white/50" />
                        <span className="text-sm font-medium">{p('all_statuses')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('active');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'active' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <CheckCircle size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{p('status_active')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('inactive');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'inactive' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <XCircle size={16} className="text-red-400" />
                        <span className="text-sm font-medium">{p('status_inactive')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="relative" ref={dateRangeRef}>
                <button 
                  onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setShowImportDropdown(false); setShowExportDropdown(false); }} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Calendar size={18} className="text-white/80" /><span className="text-white font-medium text-sm">{p('period')}</span><ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}
                  >
                    <div className="p-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-3" style={{ overflow: 'visible' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('start_date')}</label>
                            <DatePicker
                              selected={dateRange.start}
                              onChange={(date) => setDateRange({...dateRange, start: date})}
                              locale={datePickerLocale}
                              dateFormat={saudiBrand ? 'yyyy/MM/dd' : 'dd/MM/yyyy'}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                    {date.toLocaleDateString(brandLocale, { month: 'long', year: 'numeric' })}
                                  </div>
                                  <button
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('end_date')}</label>
                            <DatePicker
                              selected={dateRange.end}
                              onChange={(date) => setDateRange({...dateRange, end: date})}
                              dateFormat={saudiBrand ? 'yyyy/MM/dd' : 'dd/MM/yyyy'}
                              placeholderText={p('select_placeholder')}
                              minDate={dateRange.start}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                              renderCustomHeader={({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button
                                    onClick={decreaseMonth}
                                    disabled={prevMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                    {date.toLocaleDateString(brandLocale, { month: 'long', year: 'numeric' })}
                                  </div>
                                  <button
                                    onClick={increaseMonth}
                                    disabled={nextMonthButtonDisabled}
                                    type="button"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.5rem',
                                      borderRadius: '0.5rem',
                                      transition: 'all 0.2s',
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      width: '2rem',
                                      height: '2rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.3)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                  >
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-blue-400/30">
                          <button onClick={() => setDateRange({ start: null, end: null })} className="flex-1 px-3 py-2 bg-white/5 hover:bg-blue-500/20 text-white/80 hover:text-white rounded-xl text-sm transition-all font-medium">{p('reset')}</button>
                          <button onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl text-sm transition-all font-medium shadow-lg shadow-blue-500/30">{p('apply')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedRecords.length > 0 && (
                <button onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium">
                  <Trash2 size={16} /><span className="hidden sm:inline">{p('delete')} ({selectedRecords.length})</span>
                </button>
              )}
              
              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Upload size={16} /><span className="text-sm font-medium">{p('import_label')}</span><ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileOutput size={16} className="text-purple-400" />
                        <span className="text-sm font-medium">{t('common.format_csv')}</span>
                      </button>
                      <button onClick={handleImportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileSpreadsheet size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{t('common.format_excel')}</span>
                      </button>
                      <button onClick={handleImportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-yellow-400" />
                        <span className="text-sm font-medium">JSON</span>
                      </button>
                      <button onClick={handleImportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-orange-400" />
                        <span className="text-sm font-medium">XML</span>
                      </button>
                      <button onClick={handleImportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-gray-400" />
                        <span className="text-sm font-medium">TXT/TSV</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Download size={16} /><span className="text-sm font-medium">{p('export_label')}</span><ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50">
                        <FileText size={16} className="text-red-400" />
                        <span className="text-sm font-medium">{isGeneratingPDF ? p('generating_pdf') : t('common.format_pdf')}</span>
                      </button>
                      <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileSpreadsheet size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{t('common.format_excel')}</span>
                      </button>
                      <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileOutput size={16} className="text-purple-400" />
                        <span className="text-sm font-medium">{t('common.format_csv')}</span>
                      </button>
                      <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-yellow-400" />
                        <span className="text-sm font-medium">JSON</span>
                      </button>
                      <button onClick={handleExportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-orange-400" />
                        <span className="text-sm font-medium">XML</span>
                      </button>
                      <button onClick={handleExportHTML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-blue-300" />
                        <span className="text-sm font-medium">HTML</span>
                      </button>
                      <button onClick={handleExportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                        <FileText size={16} className="text-gray-400" />
                        <span className="text-sm font-medium">{t('common.format_txt')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('term_code') ? <TableColumnHeaderFilter columnKey="term_code" label={p('col_code')} value={filters.term_code || ''} onChange={(v) => setFilter('term_code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('label') ? <TableColumnHeaderFilter columnKey="label" label={p('col_label')} value={filters.label || ''} onChange={(v) => setFilter('label', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('days') ? <TableColumnHeaderFilter columnKey="days" label={p('col_days')} value={filters.days || ''} onChange={(v) => setFilter('days', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('discount_percent') ? <TableColumnHeaderFilter columnKey="discount_percent" label={p('col_discount')} value={filters.discount_percent || ''} onChange={(v) => setFilter('discount_percent', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_default')}</th>
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((o) => ({ value: o.value, label: o.label }))} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredPaymentTerms.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      {showCol('term_code') ? <td className="px-6 py-4"><span className="text-white font-semibold">{record.term_code}</span></td> : null}
                      {showCol('label') ? (
                      <td className="px-6 py-4">
                        <PaymentTermEditableCell
                          recordId={record.id}
                          field="label"
                          value={record.label}
                          className="text-white font-semibold"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'label'}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={startEditing}
                          onSave={saveInlineEdit}
                          onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                        />
                      </td>
                      ) : null}
                      {showCol('days') ? (
                      <td className="px-6 py-4">
                        <PaymentTermEditableCell
                          recordId={record.id}
                          field="days"
                          value={record.days != null ? String(record.days) : ''}
                          className="text-white/80 text-sm"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'days'}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={startEditing}
                          onSave={saveInlineEdit}
                          onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                        />
                      </td>
                      ) : null}
                      {showCol('discount_percent') ? (
                      <td className="px-6 py-4">
                        <PaymentTermEditableCell
                          recordId={record.id}
                          field="discount_percent"
                          value={record.discount_percent != null ? String(record.discount_percent) : ''}
                          className="text-green-300 font-semibold text-sm"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'discount_percent'}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={startEditing}
                          onSave={saveInlineEdit}
                          onCancel={cancelEditing}
                          editTitle={p('click_to_edit')}
                        />
                      </td>
                      ) : null}
                      <td className="px-6 py-4 text-center">
                        {record.is_default ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">{p('yes')}</span>
                        ) : (
                          <span className="text-white/40 text-sm">—</span>
                        )}
                      </td>
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view') || p('view') || t('common.view')}
                          editTitle={p('action_edit') || p('edit') || t('common.edit')}
                          deleteTitle={p('action_delete') || p('delete') || t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><Calendar size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('stat_total')}: {filteredPaymentTerms.length}</span>
                      <span>{p('stat_active')}: <span className="text-white font-semibold">{filteredPaymentTerms.filter((t) => t.status === 'active').length}</span></span>
                      <span>{p('with_discount_count')}: <span className="text-white font-semibold">{filteredPaymentTerms.filter((t) => parseFloat(t.discount_percent) > 0).length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredPaymentTerms.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Calendar size={20} className="text-blue-400" />{record.label}</h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_identity')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}:</span> {record.term_code}</div>
                            <div><span className="font-medium">{p('label_label')}:</span> {record.label}</div>
                            <div><span className="font-medium">{p('label_default')}:</span> {record.is_default ? p('yes') : p('no')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_terms')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2"><Clock size={14} className="text-blue-400 shrink-0" /><span className="font-medium">{p('label_days')}:</span> {record.days || '—'}</div>
                            <div><span className="font-medium">{p('label_description')}:</span> {record.description || '—'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_discount')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2"><Percent size={14} className="text-green-400 shrink-0" /><span className="font-medium">{p('label_discount_percent')}:</span> {record.discount_percent ? `${record.discount_percent}%` : '—'}</div>
                            <div><span className="font-medium">{p('label_discount_days')}:</span> {record.discount_days || '—'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <p className="text-sm text-white/70">{record.notes || '—'}</p>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Calendar size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('view_title')}
          icon={Calendar}
          maxWidth="1100px"
          testId="payment-term-view-modal"
        >
              <div className="space-y-4">
                <div className={`${PAYMENT_TERM_SECTION} p-5`}>
                  <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2"><Calendar size={18} className="text-blue-500" />{p('section_identity')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-gray-500 text-sm mb-1">{p('label_code')}</p><p className="text-gray-900 font-medium">{viewingRecord.term_code}</p></div>
                    <div><p className="text-gray-500 text-sm mb-1">{p('label_label')}</p><p className="text-gray-900 font-medium">{viewingRecord.label}</p></div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">{p('col_status')}</p>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${viewingRecord.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {viewingRecord.status === 'active' ? p('status_active') : p('status_inactive')}
                      </span>
                    </div>
                    <div><p className="text-gray-500 text-sm mb-1">{p('label_default')}</p><p className="text-gray-900 font-medium">{viewingRecord.is_default ? p('yes') : p('no')}</p></div>
                    <div className="md:col-span-2"><p className="text-gray-500 text-sm mb-1">{p('label_description')}</p><p className="text-gray-900 font-medium">{viewingRecord.description || '-'}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`${PAYMENT_TERM_SECTION} p-5`}>
                    <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2"><Clock size={18} className="text-blue-500" />{p('section_terms')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_days')}</p><p className="text-gray-900 font-medium">{viewingRecord.days || 0} {p('days_unit')}</p></div>
                    </div>
                  </div>
                  <div className={`${PAYMENT_TERM_SECTION} p-5`}>
                    <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2"><Percent size={18} className="text-green-500" />{p('section_discount')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-gray-500 text-sm mb-1">{p('col_discount')}</p><p className="text-gray-900 font-medium">{viewingRecord.discount_percent || 0}%</p></div>
                      <div><p className="text-gray-500 text-sm mb-1">{p('label_discount_days')}</p><p className="text-gray-900 font-medium">{viewingRecord.discount_days || 0} {p('days_unit')}</p></div>
                    </div>
                  </div>
                </div>
                {viewingRecord.notes && (
                  <div className={`${PAYMENT_TERM_SECTION} p-5`}>
                    <h3 className="text-gray-900 font-semibold mb-4">{p('section_notes')}</h3>
                    <p className="text-gray-700">{viewingRecord.notes}</p>
                  </div>
                )}
              </div>
        </QawaemErpViewModal>
      ) : null}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('edit_title') : p('create_title')}
          icon={Calendar}
          formId="payment-term-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          onSubmit
          testId="payment-term-crud-modal"
          tabs={paymentTermsFormTabs}
          activeTab={paymentTermsFormTab}
          onTabChange={setPaymentTermsFormTab}
        >
              <form id="payment-term-form" onSubmit={handleSubmit}>
                {paymentTermsFormTab === 'identity' && (
                  <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_code_label')}</label>
                            <input type="text" value={formData.term_code} onChange={(e) => setFormData({ ...formData, term_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('form_code_ph')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('form_label_required')} /></label>
                            <input type="text" required value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('form_label_ph')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_status')}</label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'active', label: p('status_active'), isDefault: true },
                                { value: 'inactive', label: p('status_inactive'), isDefault: true },
                              ]}
                              placeholder={p('form_status_ph')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_description')}</label>
                            <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('form_description_ph')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_default')}</label>
                            <CustomSelect
                              value={formData.is_default}
                              onChange={(value) => setFormData({ ...formData, is_default: value })}
                              options={[
                                { value: '', label: p('no'), isDefault: true },
                                { value: 'true', label: p('yes'), isDefault: true },
                              ]}
                              placeholder={p('form_default_ph')}
                              searchable={false}
                              creatable={false}
                              autoSelect={false}
                            />
                          </div>
                        </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_days')} <span className="qawaem-required-mark">*</span></label>
                            <CustomNumberInput value={formData.days} onChange={(value) => setFormData({ ...formData, days: value })} placeholder="30" step={1} min={0} decimals={0} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_discount_percent')}</label>
                            <CustomNumberInput value={formData.discount_percent} onChange={(value) => setFormData({ ...formData, discount_percent: value })} placeholder="0" step={0.01} min={0} max={100} decimals={2} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_discount_days')}</label>
                            <CustomNumberInput value={formData.discount_days} onChange={(value) => setFormData({ ...formData, discount_days: value })} placeholder="0" step={1} min={0} decimals={0} />
                          </div>
                          <div className="md:col-span-3">
                            <p className="text-white/50 text-sm">{p('form_days_hint')}</p>
                          </div>
                        </div>
                  </div>
                )}

                {paymentTermsFormTab === 'notes' && (
                  <div className="p-4 pt-0">
                          <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-y" placeholder={p('form_notes_ph')} />
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}
    </div>
  );
};

export default ConditionsPaiementPage;


